import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Table, Tag } from 'antd';
import { FiRefreshCcw, FiShoppingCart, FiUser } from 'react-icons/fi';
import { MdOutlineRequestPage } from 'react-icons/md';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import sellRequestService from '../../../services/sellRequestService';
import StatCard from '../../../components/StatCard/StatCard';

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function formatInt(value) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(toNumber(value));
}

function formatAmount(value) {
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(toNumber(value));
}

export default function NewSellRequestPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    total_requests: 0,
    total_quantity: 0,
    total_asking_price: 0,
    partner_requests: 0,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchRequests = useCallback(async (page = 1, limit = 10) => {
    setLoading(true);
    setError('');
    try {
      const data = await sellRequestService.getAdminSellRequests({ page, limit });
      const requestList = Array.isArray(data?.requests) ? data.requests : [];
      const nextPagination = data?.pagination || {};
      setRows(requestList);
      setStats({
        total_requests: toNumber(data?.stats?.total_requests),
        total_quantity: toNumber(data?.stats?.total_quantity),
        total_asking_price: toNumber(data?.stats?.total_asking_price),
        partner_requests: toNumber(data?.stats?.partner_requests),
      });
      setPagination({
        current: toNumber(nextPagination.page) || page,
        pageSize: toNumber(nextPagination.limit) || limit,
        total: toNumber(nextPagination.total) || requestList.length,
      });
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || e?.message || 'Failed to fetch sell requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const goToCreatorProfile = useCallback(
    (record) => {
      if (!record) return;
      const type = String(record.created_by_type || '').toUpperCase();
      const firstPartnerId = Array.isArray(record.partner_ids) ? record.partner_ids[0] : '';

      if (type === 'PARTNER') {
        const partnerId = firstPartnerId || record.created_by_id;
        if (!partnerId) return;
        navigate(`/partner/details/${encodeURIComponent(String(partnerId))}`);
        return;
      }

      if (type === 'INVESTOR' && record.created_by_id) {
        const query = firstPartnerId ? `?partner_id=${encodeURIComponent(String(firstPartnerId))}` : '';
        navigate(`/investors/${encodeURIComponent(String(record.created_by_id))}/profile${query}`);
      }
    },
    [navigate],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Share Name',
        dataIndex: 'share_name',
        key: 'share_name',
        render: (value) => <span className="font-medium text-gray-900">{value || '—'}</span>,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        align: 'right',
        render: (value) => <span className="text-blue-700 font-semibold">{formatInt(value)}</span>,
      },
      {
        title: 'Asking Price',
        dataIndex: 'asking_price',
        key: 'asking_price',
        align: 'right',
        render: (value) => <span className="text-emerald-700 font-semibold">{formatAmount(value)}</span>,
      },
      {
        title: 'Created By',
        dataIndex: 'created_by_name',
        key: 'created_by_name',
        render: (name, record) => (
          <button
            type="button"
            className="text-blue-700 hover:text-blue-800 hover:underline font-medium"
            onClick={(event) => {
              event.stopPropagation();
              goToCreatorProfile(record);
            }}
          >
            {name || '—'}
          </button>
        ),
      },
      {
        title: 'Created By Type',
        dataIndex: 'created_by_type',
        key: 'created_by_type',
        render: (type) => {
          const normalized = String(type || '').toUpperCase();
          const color = normalized === 'PARTNER' ? 'green' : normalized === 'INVESTOR' ? 'blue' : 'default';
          return <Tag color={color}>{normalized || 'UNKNOWN'}</Tag>;
        },
      },
      {
        title: 'Created On',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (value) => (value ? dayjs(value).format('DD-MMM-YYYY HH:mm') : '—'),
      },
    ],
    [goToCreatorProfile],
  );

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Sell Request</h1>
          <p className="text-sm text-gray-500 mt-1">View and manage sell requests from partners and investors.</p>
        </div>
        <Button icon={<FiRefreshCcw />} onClick={() => fetchRequests(pagination.current, pagination.pageSize)} loading={loading}>
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Unable to load sell requests"
          description={error}
        />
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Requests"
          value={formatInt(stats.total_requests)}
          icon={<MdOutlineRequestPage />}
          cardClass="border-blue-100 bg-blue-50/40"
          valueClass="text-blue-700"
        />
        <StatCard
          label="Total Quantity"
          value={formatInt(stats.total_quantity)}
          icon={<FiShoppingCart />}
          cardClass="border-green-100 bg-green-50/40"
          valueClass="text-green-700"
        />
        <StatCard
          label="Total Asking Price"
          value={formatAmount(stats.total_asking_price)}
          icon={<RiMoneyDollarCircleLine />}
          cardClass="border-amber-100 bg-amber-50/40"
          valueClass="text-amber-700"
        />
        <StatCard
          label="Partner Requests"
          value={formatInt(stats.partner_requests)}
          icon={<FiUser />}
          cardClass="border-purple-100 bg-purple-50/40"
          valueClass="text-purple-700"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <Table
          columns={columns}
          dataSource={rows}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          onChange={(nextPagination) => {
            fetchRequests(nextPagination.current, nextPagination.pageSize);
          }}
          rowClassName="hover:bg-gray-50 cursor-pointer"
          onRow={(record) => ({
            onClick: () => goToCreatorProfile(record),
          })}
          locale={{ emptyText: 'No sell requests found' }}
        />
      </div>
    </DashboardPageWrapper>
  );
}
